import { createContext } from 'react'
import { notFound, redirect } from '@tanstack/react-router'
import { $client, setSessionToken } from './fetch'
import { usePersistentState } from './helpers'
import type { AnyContext } from '@tanstack/react-router'
import type { CredentialResponse } from '@react-oauth/google'
import { toast } from '@/components/ui'

export interface UserSession {
  email: string
  expiry: string
  token: string
  admin: boolean
  is_oauth: boolean
}

export function isSessionExpired(user: UserSession): boolean {
  return Date.parse(user.expiry) <= Date.now()
}

/**
 * Reads the current user session directly from localStorage. Safe to call
 * outside the React tree (e.g. from imperative toast helpers). Returns null
 * if there is no session or if it has already expired.
 */
export function getCurrentSession(): UserSession | null {
  try {
    const raw = localStorage.getItem('user_data')
    if (!raw) return null
    const parsed = JSON.parse(raw) as UserSession
    if (isSessionExpired(parsed)) return null
    return parsed
  } catch {
    return null
  }
}

export const LoginContext = createContext<LoginContextValue | null>(null)

type loginFunc = (user: UserSession) => void

export interface LoginContextValue {
  user: UserSession | null
  logout: () => void
  login: loginFunc
  logOAuth: (resp: CredentialResponse) => void
}

export const validatePassword = (password: string): string | null => {
  if (password.length < 8) {
    return 'Le mot de passe doit contenir au moins 8 caractères'
  }
  if (!password.match(/[A-Z]/)) {
    return 'Le mot de passe doit contenir au moins une lettre majuscule'
  }
  if (!password.match(/[0-9]/)) {
    return 'Le mot de passe doit contenir au moins un chiffre'
  }
  if (!password.match(/[^a-zA-Z0-9]/)) {
    return 'Le mot de passe doit contenir au moins un caractère spécial'
  }
  return null
}

// Patch set user to set the token properly. Also drops any persisted session
// whose token has already expired so the app boots as logged-out.
export const useUser = () =>
  usePersistentState<UserSession | null>(
    'user_data',
    null,
    (prevUser) => setSessionToken(prevUser?.token ?? null),
    (prevUser) => prevUser === null || !isSessionExpired(prevUser),
  )

export function loginWithGoogle(
  login: loginFunc,
): (resp: CredentialResponse) => void {
  return async ({ credential: id_token }) => {
    if (!id_token) {
      toast({
        color: 'danger',
        title: "Impossible de s'enregistrer via Google",
      })
      return
    }
    const { data } = await $client.POST('/api/auth/google', {
      body: { id_token },
    })

    if (!data?.session_token) {
      toast({
        color: 'danger',
        title: 'Token de connexion invalide',
      })
      return
    }

    login({
      ...data,
      token: data.session_token,
    })
    toast({
      color: 'success',
      title: 'Connecté !',
    })
  }
}

export function loggedInRouteLoader({
  context,
  location,
}: {
  context: AnyContext
  location: Location
}) {
  const userSession: UserSession | null = (context as any)?.user

  if (!userSession) {
    // Redirect to not found if not authenticated
    throw redirect({ to: LOGIN, search: { go: location.pathname } })
  }
  return null
}

export function adminRouteLoader({ context }: { context: AnyContext }) {
  const userSession: UserSession | null = (context as any)?.user

  if (!userSession?.admin) {
    // Redirect to not found if not authenticated
    throw notFound()
  }
  return null
}

export const LOGIN = '/login'
